import DomainCard from '@/components/domains/DomainCard'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { EmptyState } from '@/components/patterns/empty-state'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { domainQueries, useDomains } from '@/lib/services/domains/domains.query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { Globe } from 'lucide-react'

function sortDomains(a, b) {
  if (a?.is_primary !== b?.is_primary) {
    return a?.is_primary ? -1 : 1
  }

  const aInactive = !a?.verified_at
  const bInactive = !b?.verified_at

  if (aInactive !== bInactive) {
    return aInactive ? 1 : -1
  }

  return String(a?.domain ?? '').localeCompare(String(b?.domain ?? ''))
}

export const Route = createFileRoute('/_authenticated/$orgId/website/domains/')({
  component: DomainsPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'domains')) throw notFound()

    await context.queryClient.ensureQueryData(domainQueries.index({ organizationId: params.orgId }))
  },
})

export function DomainsPage() {
  const { data: domains } = useDomains()
  const path = usePathHelper()
  const sortedDomains = [...(domains ?? [])].sort(sortDomains)

  return (
    <div className="max-w-6xl space-y-5">
      <AdminPageHeader
        icon={Globe}
        title="Domains"
        description="Set the public website address shoppers use to find this store. If more than one domain is connected, every address sends shoppers to the primary domain so they always land in the same place."
        actions={
          <IfGlobalOrg show={false}>
            <Can I="create" a="domains">
              <CreateActionButton asChild>
                <Link to={path.domains.create().href}>Create New Domain</Link>
              </CreateActionButton>
            </Can>
          </IfGlobalOrg>
        }
      />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice>
            Domains are organization-specific. Manage domains inside the right florist account.
          </OrganizationContextNotice>
        }
      >
        {sortedDomains.length ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-4">
              {sortedDomains.map((domain) => (
                <DomainCard key={domain.id} domain={domain} />
              ))}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={Globe}
            title="No domains found."
            description="Add a custom web address when this shop is ready to point customers to its storefront."
            action={
              <Can I="create" a="domains">
                <Button asChild variant="outline">
                  <Link {...path.domains.create().link}>Create your first domain</Link>
                </Button>
              </Can>
            }
          />
        )}
      </IfGlobalOrg>
    </div>
  )
}

export default DomainsPage
